import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CsvImportResultDto, PartenaireDto, PartenaireType, PartenairesLexiClient, Permissions, ProfilPartenaireDto, ProfilPartenairesLexiClient } from '@lexi-clients/lexi';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import notify from "devextreme/ui/notify";
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { NotificationType } from 'lexi-angular/src/app/modules/shared/references/references';
import { AuthService } from 'lexi-angular/src/app/settings/auth.service';
import { lastValueFrom } from 'rxjs';
import { PartenaireCategorieEnum, partenaireCategories, partenaireCategoriesLibellesMap } from '../../../models/enums/partenaireCategorie';
import { DownloadService } from '../../../services/download.service';
import { DxDataSourceService } from '../../../shared/services/dx-data-source.service';
import { PartenaireNewFormComponent } from '../partenaire-new-form/partenaire-new-form.component';

@Component({
    selector: 'app-partenaire-list',
    templateUrl: './partenaire-list.component.html',
    styleUrls: ['./partenaire-list.component.scss'],
    standalone: false
})
export class PartenaireListComponent implements OnInit {
  @ViewChild('gridContainer', { static: false }) dataGrid: DxDataGridComponent;
  @ViewChild(PartenaireNewFormComponent, { static: false }) partenaireNewForm: PartenaireNewFormComponent;

  dataSource: CustomStore;
  initialized = false;
  showImportCsvPopup = false;
  pattern: string;
  fichierCsv: File[] = [];
  importCsvResultMsg: string;
  importCsvResultMsg2: string;
  importHasFailed = false;
  showLoader = false;
  partenaireCategorie = PartenaireCategorieEnum.clients;
  partenaireCategories = partenaireCategories;
  isPartenaireCategorieFige: boolean = true;
  recuperationTemplateEncours = false;
  showLegende = false;
  estClient: boolean | null;
  estFournisseur: boolean | null;
  estPartenaire: boolean | null;
  grilleStorageKey = "grilles_partenaire_list_" + this.partenaireCategorieIntitule;
  isAddPartenairePopupOpened: boolean = false;
  profilPartenaireDataSource: ProfilPartenaireDto[] = [];
  canAdministrerClientsPermission : boolean = false;
  constructor(
    private readonly partenairesLexiClient: PartenairesLexiClient,
    private readonly dxDataSourceService: DxDataSourceService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly downloadService: DownloadService,
    private readonly profilPartenairesLexiClient: ProfilPartenairesLexiClient,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) { }

  ngOnInit() {
    this.activatedRoute.url.subscribe(async (urlSegment) => {
      const partenaireCategorieFromUrl: string = urlSegment[0].path;
      switch (partenaireCategorieFromUrl) {
        case "clients": {
          this.partenaireCategorie = PartenaireCategorieEnum.clients;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "fournisseurs": {
          this.partenaireCategorie = PartenaireCategorieEnum.fournisseurs;
          this.isPartenaireCategorieFige = true;
          break;
        }
        case "partenaires": {
          this.partenaireCategorie = PartenaireCategorieEnum.internes;
          this.isPartenaireCategorieFige = false;
          this.partenaireCategories = partenaireCategories.filter(x => x.enumId !== PartenaireCategorieEnum.clients && x.enumId !== PartenaireCategorieEnum.fournisseurs);
          break;
        }
      }

      if (!this.initialized) {
        this.profilPartenaireDataSource = await lastValueFrom(this.profilPartenairesLexiClient.getAll());
        this.setDataSource();
        this.initialized = true;
      }
      this.canAdministrerClientsPermission = this.authService.securityUserisGrantedWith(Permissions.canAdministrerClients);
    });
  }

  get partenaireCategorieIntitule(): string {
    return partenaireCategoriesLibellesMap.get(this.partenaireCategorie);
  }

  setDataSource() {
    this.estClient = this.partenaireCategorie == PartenaireCategorieEnum.clients ? true : null;
    this.estFournisseur = this.partenaireCategorie == PartenaireCategorieEnum.fournisseurs ? true : null;
    const partenaireType: PartenaireType | null = this.partenaireCategorie == PartenaireCategorieEnum.internes ? PartenaireType.interne
      : this.partenaireCategorie == PartenaireCategorieEnum.externes ? PartenaireType.externe
      : null;
    this.dataSource = this.dxDataSourceService.getPartenaireCustomStore(this.estClient, this.estFournisseur, partenaireType, this.pattern);
  }

  clearDatagridFilters() {
    this.dataGrid?.instance.clearFilter();
  }

  async importDataFromCsv() {
    this.resetImportResultMsg();
    this.showLoader = true;
    try {
      const response: CsvImportResultDto = await lastValueFrom(this.partenairesLexiClient.importPartenairesFromCsv(true, this.fichierCsv[0]));

      // Erreur
      if (response.hasFailed) {
        this.importHasFailed = true;
        if (response.errorMessage != null) {
          this.importCsvResultMsg = response.errorMessage;
        }

        else {
          if (response.hasWrongHeadersCount) {
            this.importCsvResultMsg = "Le nombre de colonnes est incorrecte, il y a plus de colonnes que celles attendues.";
          }

          else {
            this.importCsvResultMsg = `Les colonnes suivantes sont invalides: ${response.invalidHeaders.join(', ')}.`;
          }

          if (response.expectedHeaders != null) {
            this.importCsvResultMsg2 = `Colonnes attendues: (${response.expectedHeaders.join(', ')})`;
          }
        }
      }

      else if (response.itemsOnError.length > 0) {
        let errorMessage = "";
        response.itemsOnError.forEach(x => errorMessage += `${x}\n`);
        this.importCsvResultMsg = `Certaines éléments n'ont pas pu être importées. Le détail a été copié dans le presse papier`;
        this.importCsvResultMsg2 = "Importation incomplète";
        navigator.clipboard.writeText(errorMessage).then().catch(e => console.error(e));
        this.setDataSource();
      }

      // Succès
      else {
        this.importHasFailed = false;
        this.setDataSource();
        this.importCsvResultMsg = `${response.nbItemsCreated} élément(s) ont été créé(s), ${response.nbItemsUpdated} élément(s) ont été mis à jour.\nL'importation s'est terminée.`;
      }

      this.fichierCsv = [];
    }
    finally {
      this.showLoader = false;
    }
  }

  cancelImportCsv() {
    this.showImportCsvPopup = false;
    this.resetImportResultMsg();
    this.fichierCsv = [];
  }

  resetImportResultMsg() {
    this.importCsvResultMsg = null;
    this.importCsvResultMsg2 = null;
  }

  onRowPrepared(e) {
    if (!e.data?.actif && e.rowType == "data") {
      e.rowElement.classList.add("row-bloque");
    }
  }

  onPartenaireCategoryChanged(e: { value: PartenaireCategorieEnum }) {
    this.partenaireCategorie = e.value;
    this.setDataSource();
  }

  async downloadTemplate() {
    try {
      this.recuperationTemplateEncours = true;
      const response = await lastValueFrom(this.partenairesLexiClient.getTemplateImportCsv('response'));
      this.downloadService.directDownloadFile(response, 'csv');
    }
    finally {
      this.recuperationTemplateEncours = false;
    }
  }

  onExporting(e: ExportingEvent) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Partenaires');

    exportDataGrid({
      component: e.component,
      worksheet,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data') {

          // Code
          if (gridCell.column.caption === 'Code' && gridCell.value == null) {
            excelCell.value = gridCell.data.codeBoClient ?? gridCell.data.codeBo;
          }
        }
      },
    }).then(() => {
      const date = formatDate((new Date), "yyyyMMdd-HHmm", "fr-FR");
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${date}-${this.partenaireCategorieIntitule}.xlsx`);
      });
    });
  }

  addPartenaire() {
    this.isAddPartenairePopupOpened = true;
  };

  onClickSaveNewContact() {
    this.isAddPartenairePopupOpened = false;
  }

  onClickSaveNewPartenaire = async () => {
    const newPartenaire: PartenaireDto = this.partenaireNewForm.getNewPartenaireData();
    if(!newPartenaire) return;

    try {
      newPartenaire.estFournisseur ??= false;
      await lastValueFrom(this.partenairesLexiClient.create(newPartenaire));
      notify({ closeOnClick: true, message: `Nouveau partenaire "${newPartenaire.codeBo} ${newPartenaire.intitule}" créé.` }, NotificationType.Success);
    }
    finally {
      this.setDataSource();
    }
  }

  navigateToDoublons() {
    this.router.navigate(['/clients/doublons']);
  }
}
